import { MGPValidation, ReversibleMap, Utils } from '@everyboard/lib';
import { Coord } from '../../jscaip/Coord';
import { CoordSet } from '../../jscaip/CoordSet';
import { HexagonalUtils } from '../../jscaip/HexagonalUtils';
import { Player, PlayerOrNone } from '../../jscaip/Player';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { Vector } from '../../jscaip/Vector';
import { OpenHexagonalGameState } from '../../jscaip/state/OpenHexagonalGameState';
import { SixFailure } from './SixFailure';
export class SixState extends OpenHexagonalGameState {
    /**
      * @param board the representation of the board
      * @param turn the turn of the board
      * @param origin the coord of the board[0][0] space
      * (useful if the upper left coord is in (-5, -9) or (512, 129))
      * @returns the state created from that board
     */
    static ofRepresentation(board, turn, origin = new Vector(0, 0)) {
        const pieces = new ReversibleMap();
        for (let y = 0; y < board.length; y++) {
            for (let x = 0; x < board[0].length; x++) {
                if (board[y][x] !== PlayerOrNone.NONE) {
                    const adapted = new Coord(x, y).getNext(origin);
                    pieces.set(adapted, board[y][x]);
                }
            }
        }
        return new SixState(pieces, turn);
    }
    movePiece(move) {
        const pieces = this.pieces.getCopy();
        pieces.delete(move.start.get());
        pieces.set(move.landing, this.getCurrentPlayer());
        return new SixState(pieces, this.turn);
    }
    toRepresentation() {
        const board = TableUtils.create(this.width, this.height, PlayerOrNone.NONE);
        for (const piece of this.pieces.getKeyList()) {
            const pieceValue = this.getPieceAt(piece);
            board[piece.y][piece.x] = pieceValue;
        }
        return board;
    }
    isIllegalLandingZone(landing, start) {
        if (this.pieces.containsKey(landing)) {
            return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        if (this.isCoordConnected(landing, start)) {
            return MGPValidation.SUCCESS;
        }
        else {
            return MGPValidation.failure(SixFailure.MUST_DROP_NEXT_TO_OTHER_PIECE());
        }
    }
    isCoordConnected(coord, except) {
        for (const neighbor of HexagonalUtils.getNeighbors(coord)) {
            if (this.pieces.containsKey(neighbor) &&
                (except.equalsValue(neighbor) === false)) {
                return true;
            }
        }
        return false;
    }
    getPieceAt(coord) {
        if (this.isOnBoard(coord)) {
            return this.pieces.get(coord).get();
        }
        else {
            return PlayerOrNone.NONE;
        }
    }
    applyLegalDrop(coord) {
        const pieces = this.pieces.getCopy();
        pieces.put(coord, this.getCurrentPlayer());
        return new SixState(pieces, this.turn + 1);
    }
    applyLegalTranslation(move, kept) {
        const stateAfterMove = this.movePiece(move);
        if (kept.size() > 0) {
            const newPieces = new ReversibleMap();
            for (const coord of kept) {
                newPieces.set(coord, stateAfterMove.getPieceAt(coord));
            }
            return new SixState(newPieces, this.turn + 1);
        }
        else {
            return new SixState(stateAfterMove.pieces, this.turn + 1);
        }
    }
    countPiecesOnBoard() {
        const pieces = this.pieces.reverse();
        const zeroPieces = pieces.get(Player.ZERO).getOrElse(new CoordSet());
        const onePieces = pieces.get(Player.ONE).getOrElse(new CoordSet());
        return PlayerNumberMap.of(zeroPieces.size(), onePieces.size());
    }
    countPiecesToDrop(config) {
        const total = config.piecesPerPlayer + 1;
        const piecesOnBoard = this.countPiecesOnBoard();
        return PlayerNumberMap.of(total - piecesOnBoard.get(Player.ZERO), total - piecesOnBoard.get(Player.ONE));
    }
    switchPiece(coord) {
        const newPieces = this.pieces.getCopy();
        const oldPiece = this.getPieceAt(coord);
        Utils.assert(oldPiece.isPlayer(), 'Cannot switch piece if there is no piece!', { coord: coord.toString() });
        newPieces.replace(coord, oldPiece.getOpponent());
        return new SixState(newPieces, this.turn);
    }
    equals(other) {
        return this.turn === other.turn && this.pieces.equals(other.pieces);
    }
}
